'use client';
import React, { useState, useEffect, useMemo } from 'react';
import { ICommit } from '@/types';
import { useForm, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import Button from '@/ui/Button';
import * as Icon from '@/ui/Icon';

import { uploadFiles } from '@/lib/workspaces/filesApi';
import FileUploader from '@/components/FileUploaer';

import { colors } from '@/styles';
import { IRepository, IUser } from '@/types';
import { Row, Stack, Card } from '@/ui/primatives';
import Input from '@/components/Input';
import Tag from '@/ui/Tag';
import * as format from '@/lib/format';

import { IResponseError } from '@/lib/apiBase';

import DownloadProgressIndicator from '@/components/DownloadProgressIndicator';
import { TDownloadProgress } from '@/lib/repositoryFileApi';
import tracking from '@/lib/tracking';
// import { DevTool } from '@hookform/devtools';

type FormInputs = {
  file: File[];
  directory?: string;
  commitMessage: string;
};

const formSchema = Yup.object().shape({
  commitMessage: Yup.string().required('Required'),
  directory: Yup.string(),
  file: Yup.array().min(1, 'at least 1 file is required').required('required'),
});

const LENGTH_LIMIT = 23

export type TFileUploadFormCompleteData = {
  repository: IRepository;
  branch: string;
  targetDirectory?: string;
  fileNames: string[];
  commit: ICommit;
};

type TFileUploadFormProps = {
  user: IUser;
  initialFiles?: File[];
  repository: IRepository;
  branch?: string;
  maxFiles?: number;
  resourcePath?: string;
  testProgress?: TDownloadProgress;
  onComplete: (data: TFileUploadFormCompleteData) => void;
};

const FileUploadForm: React.FC<TFileUploadFormProps> = ({
  initialFiles,
  repository,
  branch = repository.default_branch || 'main',
  maxFiles = 0,
  resourcePath,
  testProgress = null,
  onComplete,
}) => {
  const [myFiles, setFiles] = useState<File[]>(initialFiles || []);
  const [systemError, setSystemError] = useState<IResponseError>();
  const [targetDirectory, setTargetDirectory] = useState<string | undefined>(resourcePath);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [progress, setProgress] = useState<TDownloadProgress | null>(testProgress);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(formSchema),
    reValidateMode: 'onChange',
  });
  const watchDirectory = watch('directory');
  const watchFiles = watch('file');

  const fileNames = useMemo(() => {
    return myFiles.map((file) => file.name);
  }, [myFiles]);

  useEffect(() => {
    setTargetDirectory([resourcePath, watchDirectory].filter((val) => val).join('/'));
  }, [resourcePath, watchDirectory]);

  useEffect(() => {
    setFiles(watchFiles || []);
  }, [watchFiles]);

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    const targetDirectory = data.directory
      ? [resourcePath, data.directory].filter((val) => val).join('/')
      : resourcePath;

    setSubmitting(true);
    setStatusMessage('Uploading files...');

    const { status, error, commit } = await uploadFiles({
      files: data.file,
      repository: repository,
      branch: branch,
      commitMessage: data.commitMessage,
      resourcePath: targetDirectory,
      onProgress: setProgress,
    });

    if (status === 'success') {
      tracking.capture('file_upload');
      setStatusMessage('Complete. Redirecting...');
      onComplete({ repository, branch, targetDirectory, fileNames, commit });
    } else if (status == 'error' && error) {
      console.log('error', error);
      setStatusMessage('Error uploadings. Please try again.');
      setSystemError(error);
      setSubmitting(false);
    } else {
      setSubmitting(false);
      setStatusMessage(null);
    }
  };
  
  const tooltipContent = resourcePath && resourcePath.length > LENGTH_LIMIT ? { content: resourcePath } : undefined;

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={20}>
          <FileUploader control={control} maxFiles={maxFiles} errors={errors} setValue={setValue} />
          <Input
            prefixElement={<Tag tooltip={tooltipContent} variant="info">{resourcePath && format.compactStringWithEllipsis(resourcePath, LENGTH_LIMIT)}/</Tag>}
            label="Target directory"
            name="directory"
            submit={register}
            error={errors.directory}
            hint="If you want add the files in a new subdirectory"
          />

          {myFiles.length > 0 && (
            <Card style={{ background: colors.semantic.information.light }} snug stretch>
              <div>
                You're about to commit {myFiles.length} files directly to the{' '}
                <b className="mono">{targetDirectory || 'root'}</b> directory on the <b className="mono">{branch}</b>{' '}
                branch.
              </div>
            </Card>
          )}

          <Input
            style={{ flex: 1 }}
            wide
            label="Commit Message"
            name="commitMessage"
            submit={register}
            error={errors.commitMessage}
          />

          {systemError && (
            <Row gap={10} style={{ color: colors.semantic.negative.dark }}>
              <Icon.XCircle />
              <span>{systemError.detail}</span>
            </Row>
          )}
          <Row gap={20}>
            <Button.Black small disabled={submitting}>
              {submitting ? 'Uploading Files...' : 'Commit Files'}
            </Button.Black>
            {/* <Button.Secondary small>
              Cancel
            </Button.Secondary> */}
          </Row>
          {statusMessage && <div>{statusMessage}</div>}
          {progress && <DownloadProgressIndicator progress={progress} />}
        </Stack>
      </form>
      {/* <DevTool control={control} /> */}
    </div>
  );
};

export default FileUploadForm;
